import axios from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { message } from 'antd';
import { API_CONFIG, API_STATUS_CODES, STORAGE_KEYS, TOKEN_CONFIG } from '@/config/api';
// import { useAppStore } from '@/stores/app';
// import { useAuthStore } from '@/stores/authStore';
import userManager from '@/hooks/oidcConfig';
import { jwtDecode } from 'jwt-decode';

// 请求队列管理
interface PendingRequest {
  url: string;
  cancel: () => void;
}

const pendingRequests = new Map<string, () => void>();

// Token刷新状态管理
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;
let requests: Array<(token: string) => void> = [];
let lastRefreshTime = 0;

// 获取UTC时间（毫秒）
const getUTCTime = (): number => Date.now();

// 创建axios实例
const service: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.defaultHeaders
});

// Token刷新函数
const refreshToken = async (refreshTokenStr: string): Promise<any> => {
  // 本地开发环境跳过token刷新
  if (import.meta.env.MODE === 'development') {
    const mockToken = 'be8cb528e6c8466e8866eafe3a55ac8e';
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, mockToken);
    return Promise.resolve({ data: { access_token: mockToken } });
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  const params = {
    client_id: import.meta.env.VITE_OIDC_CLIENT_ID,
    grant_type: 'refresh_token',
    client_secret: import.meta.env.VITE_OIDC_CLIENT_SECRET,
    refresh_token: refreshTokenStr,
    scope: 'api openid profile offline_access'
  };

  refreshPromise = axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/auth/token`, 
    new URLSearchParams(params), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    })
    .then(res => {
      refreshPromise = null;
      lastRefreshTime = getUTCTime();
      return res;
    });

  return refreshPromise;
};

// 检查token是否需要刷新
const isTokenNeedRefresh = (tokenObj: any): boolean => {
  if (!tokenObj?.expires_at) return false;
  
  const currentTime = Math.floor(getUTCTime() / 1000);
  const willExpireSoon = Number(tokenObj.expires_at) < (currentTime + TOKEN_CONFIG.EXPIRE_THRESHOLD);
  const debounceCondition = (getUTCTime() - lastRefreshTime) > TOKEN_CONFIG.REFRESH_DEBOUNCE;
  
  return willExpireSoon && debounceCondition;
};

// 取消待处理的请求
const cancelPending = (config: InternalAxiosRequestConfig): void => {
  const requestKey = config.url as string;
  if (pendingRequests.has(requestKey)) {
    const cancel = pendingRequests.get(requestKey);
    cancel?.();
    pendingRequests.delete(requestKey);
  }
};

// 统一处理响应错误
const handleResponseError = (response: AxiosResponse): void => {
  if (!response?.data?.res_status_code) return;
  if(response.data.res_status_code === '0'){
    return response.data
  }else {
    message.error(response.data.res_message);
  }

  // if ([API_STATUS_CODES.UNAUTHORIZED, API_STATUS_CODES.FORBIDDEN].includes(response.data.res_status_code)) {
  //   message.error(response.data.res_message);

  //   if (response.data.res_status_code === API_STATUS_CODES.UNAUTHORIZED) {
  //     const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  //     if (token) {
  //       // useAppStore.getState().logout();
  //     } else {
  //       location.reload();
  //     }
  //   }

  //   if (response.data.res_status_code === API_STATUS_CODES.FORBIDDEN) {
  //     const errorUrl = import.meta.env.VITE_ERROR_HTML_URL || '/error';
  //     window.location.href = errorUrl;
  //   }
  // }
};

// 请求拦截器
service.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // 开发环境直接使用固定token
    if (import.meta.env.MODE === 'development') {
      const mockToken = 'be8cb528e6c8466e8866eafe3a55ac8e';
      config.headers = config.headers || {};
      config.headers.token = `${mockToken}`;
      return config;
    }

    let token = null;
    const skipSso = import.meta.env.VITE_SKIP_SSO === 'true';

    if (skipSso ) {
      const mockToken = 'be8cb528e6c8466e8866eafe3a55ac8e';
      config.headers = config.headers || {};
      config.headers.token = `${mockToken}`;
      return config;
    }

    try {
      const tokenObjStr = localStorage.getItem(STORAGE_KEYS.TOKEN_OBJ);
      
      if (tokenObjStr) {
        const tokenObj = JSON.parse(tokenObjStr);
        
        if (localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) && 
            !isRefreshing && 
            isTokenNeedRefresh(tokenObj)) {
          isRefreshing = true;
          
          try {
            const res = await refreshToken(tokenObj.refresh_token);
            
            if (res?.data?.access_token) {
              localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, res.data.access_token);
              
              const jwtToken: any = jwtDecode(res.data.access_token);
              const newTokenObj = {
                expires_at: jwtToken.exp,
                refresh_token: res.data.refresh_token,
                loginIn: jwtToken.lid,
              };
              
              localStorage.setItem(STORAGE_KEYS.TOKEN_OBJ, JSON.stringify(newTokenObj));
              token = res.data.access_token;
              
              requests.forEach(cb => cb(res.data.access_token));
              requests = [];
            }
          } catch (error) {
            console.error('Token刷新失败:', error);
            localStorage.clear();
            userManager.signinRedirect({ state: window.location.href });
          } finally {
            isRefreshing = false;
          }
        } else if (isRefreshing && config.url !== API_CONFIG.endpoints.token) {
          return new Promise((resolve) => {
            requests.push((newToken) => {
              config.headers = config.headers || {};
              // config.headers.Authorization = `Bearer ${newToken}`;
              config.headers['token'] = 'be8cb528e6c8466e8866eafe3a55ac8e';
              resolve(config);
            });
          });
        } else {
          token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        }
      }
    } catch (error) {
      console.error('处理token时出错:', error);
      localStorage.clear();
      userManager.signinRedirect({ state: window.location.href });
    }

    // 验证并设置token
    if (token) {
      try {
        // 解析JWT token以验证其有效性
        const decodedToken: any = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);
        
        // 检查token是否过期
        if (decodedToken.exp && decodedToken.exp > currentTime) {
          config.headers = config.headers || {};
          // config.headers.Authorization = `Bearer ${token}`;
          config.headers['token'] = 'be8cb528e6c8466e8866eafe3a55ac8e';
        } else {
          // token已过期，清除并重定向到登录
          localStorage.clear();
          userManager.signinRedirect({ state: window.location.href });
          return Promise.reject(new Error('Token已过期'));
        }
        // config.headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
        config.headers['token'] = 'be8cb528e6c8466e8866eafe3a55ac8e';
      } catch (error) {
        console.error('Token验证失败:', error);
        localStorage.clear();
        userManager.signinRedirect({ state: window.location.href });
        return Promise.reject(new Error('Token验证失败'));
      }
    }

    // 添加页面URL到请求头
    if (config.headers) {
      config.headers['page_url'] = window.location.pathname;
      
      const sessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
      if (sessionId) {
        config.headers['J-Session-ID'] = sessionId;
      }
    }

    // 处理请求取消
    if (config.params?.isCancelRequest) {
      cancelPending(config);
      const source = axios.CancelToken.source();
      config.cancelToken = source.token;
      pendingRequests.set(config.url as string, source.cancel);
    }
    

    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    cancelPending(response.config);
    handleResponseError(response);
    return response;
  },
  async (error) => {
    if (axios.isCancel(error)) {
      console.log('请求被取消');
      return Promise.reject(error);
    }

    if (error.isAxiosError && error.response) {
      const statusCode = error.response.status;

      if (statusCode === 401) {
        console.log('Token过期或认证失败');
        const skipSso = import.meta.env.VITE_SKIP_SSO === 'true';

        if (skipSso) {
          console.log('开发模式下，清除mock token并刷新页面');
          localStorage.removeItem(STORAGE_KEYS.MOCK_TOKEN);
          window.location.reload();
          return Promise.reject(new Error('开发模式下的token失效，已刷新页面'));
        }

        // const authStore = useAuthStore.getState();
        // authStore.logout();
        return Promise.reject(error);
      }

      if (statusCode === 403) {
        console.log('权限不足');
        const errorUrl = import.meta.env.VITE_ERROR_HTML_URL;
        if (errorUrl) {
          window.location.href = errorUrl;
        }
        return Promise.reject(error);
      }

      handleResponseError(error.response);
    }

    if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
      console.error('请求超时:', error.config?.url, '超时设置:', error.config?.timeout, 'ms');
      message.error('请求超时，请重试');
    }

    return Promise.reject(error);
  }
);

// 导出请求方法
export const http = {
  get: <T>(url: string, config?: any): Promise<T> => 
    service.get(url, config).then(response => response.data),

  post: <T>(url: string, data?: any, config?: any): Promise<T> => 
    service.post(url, data, config).then(response => response.data),

  put: <T>(url: string, data?: any, config?: any): Promise<T> => 
    service.put(url, data, config).then(response => response.data),

  delete: <T>(url: string, config?: any): Promise<T> => 
    service.delete(url, config).then(response => response.data)
};

export default service; 